// lib/modelist/sun.ts
// Where the sun stands over a place at an instant, and the clock a place's date and time are read on.
import type { Fater3DPlace, Fater3DState } from '#types/types';
import { validDate, validZone } from '../../scripts/modelist/light.mjs';

const RAD = Math.PI / 180;
const DAY_MS = 86_400_000;

/** Paris: where a site with no known position starts. */
export const DEFAULT_PLACE: Fater3DPlace = { lat: 48.8566, lon: 2.3522, north: 0, zone: 'Europe/Paris' };

/** A place held on the globe: coordinates to the metre (five decimals), north within one turn. */
export const clampPlace = ({ lat, lon, north, zone }: Fater3DPlace): Fater3DPlace => ({
  lat: Math.round(Math.min(90, Math.max(-90, lat)) * 1e5) / 1e5,
  lon: Math.round(Math.min(180, Math.max(-180, lon)) * 1e5) / 1e5,
  north: ((north % 360) + 360) % 360,
  zone,
});

const utcMinutes = (at: Date) => at.getUTCHours() * 60 + at.getUTCMinutes() + at.getUTCSeconds() / 60;

// NOAA's general solar position (gml.noaa.gov/grad/solcalc/solareqns.PDF): the sun's declination, in radians, and the
// equation of time, in minutes, from the fraction of the year elapsed.
const orbit = (at: Date) => {
  const g = ((2 * Math.PI) / 365) * ((at.getTime() - Date.UTC(at.getUTCFullYear(), 0, 1)) / DAY_MS - 0.5);
  return {
    declination: 0.006918 - 0.399912 * Math.cos(g) + 0.070257 * Math.sin(g) - 0.006758 * Math.cos(2 * g) + 0.000907 * Math.sin(2 * g) - 0.002697 * Math.cos(3 * g) + 0.00148 * Math.sin(3 * g),
    equation: 229.18 * (0.000075 + 0.001868 * Math.cos(g) - 0.032077 * Math.sin(g) - 0.014615 * Math.cos(2 * g) - 0.040849 * Math.sin(2 * g)),
  };
};

/** The sun over a place at an instant, in degrees: its azimuth clockwise from true north, its elevation above the geometric horizon (negative under it). */
export const solarPosition = (at: Date, lat: number, lon: number) => {
  const { declination, equation } = orbit(at);
  const hour = ((utcMinutes(at) + equation + 4 * lon) / 4 - 180) * RAD;
  const phi = lat * RAD;
  return {
    azimuth: (Math.atan2(Math.sin(hour), Math.cos(hour) * Math.sin(phi) - Math.tan(declination) * Math.cos(phi)) / RAD + 180) % 360,
    elevation: Math.asin(Math.sin(phi) * Math.sin(declination) + Math.cos(phi) * Math.cos(declination) * Math.cos(hour)) / RAD,
  };
};

/**
 * The elevation the sun rises and sets at: its upper edge on the horizon through the standard refraction. The one
 * horizon of the light, of what the panel says and of the times it prints.
 */
export const SUN_HORIZON = -0.833;

// Half the time the sun spends above SUN_HORIZON on a day of this declination, null when it never crosses it.
const halfDay = (declination: number, lat: number) => {
  const cos = Math.sin(SUN_HORIZON * RAD) / (Math.cos(lat * RAD) * Math.cos(declination)) - Math.tan(lat * RAD) * Math.tan(declination);
  return Math.abs(cos) <= 1 ? (Math.acos(cos) / RAD) * 4 * 60_000 : null;
};

/**
 * Sunrise and sunset around the place's solar noon nearest to an instant; null on a day the sun neither rises nor
 * sets there. Each is taken with the declination of its own hour, so the sun stands on SUN_HORIZON at the time given.
 */
export const daylight = (at: Date, lat: number, lon: number): { rise: Date; set: Date } | null => {
  const { declination, equation } = orbit(at);
  const first = halfDay(declination, lat);
  if (first === null) return null;
  const noon = at.getTime() + (((((720 - utcMinutes(at) - equation - 4 * lon) % 1440) + 2160) % 1440) - 720) * 60_000;
  const edge = (side: -1 | 1) => {
    const half = halfDay(orbit(new Date(noon + side * first)).declination, lat);
    return half === null ? null : new Date(noon + side * half);
  };
  const rise = edge(-1);
  const set = edge(1);
  return rise && set ? { rise, set } : null;
};

/** 21 June 2026, the summer solstice: the date a light opens on. */
export const DEFAULT_DATE = '2026-06-21';
// A date that is none is the default one, so no NaN reaches the light.
const dayOf = (date: string) => (validDate(date) ? date : DEFAULT_DATE).split('-').map(Number) as [number, number, number];

/** The whole hours a meridian's solar time is ahead of Greenwich's: its hour of the globe. */
export const meridianHours = (lon: number) => Math.round(lon / 15);
/** The fixed zone of a meridian, as IANA names it: the Etc names count westwards, so UTC+7 is "Etc/GMT-7". */
export const meridianZone = (lon: number) => {
  const hours = meridianHours(lon);
  return hours ? `Etc/GMT${hours > 0 ? '-' : '+'}${Math.abs(hours)}` : 'UTC';
};

/**
 * The browser's own clock: its zone's name, and its offset outside summer time, in minutes ahead of UTC (summer time
 * only ever adds, in either hemisphere). The one read of the browser's zone: every function below takes it as given.
 */
export const browserClock = () => {
  const year = new Date().getFullYear();
  return { zone: new Intl.DateTimeFormat().resolvedOptions().timeZone, offset: Math.min(-new Date(year, 0, 1).getTimezoneOffset(), -new Date(year, 6, 1).getTimezoneOffset()) || 0 };
};

/**
 * The zone given to a place that names none. The app holds no map of the time zones: a place whose meridian is within
 * two hours of the viewer's own clock is taken to keep that clock, summer time included (a French site seen from
 * France), and any other gets the fixed zone of its meridian. The two hours are counted around the globe: across the
 * date line UTC+13 and UTC-11 are the same hour of the day. Decided once, then stored with the place.
 */
export const zoneFor = (lon: number, browser: { zone: string; offset: number }) => {
  const apart = (((meridianHours(lon) * 60 - browser.offset) % 1440) + 1440) % 1440;
  // A browser can name no zone a place may be saved under ("Etc/Unknown", an offset): the meridian's then.
  return Math.min(apart, 1440 - apart) <= 120 && validZone(browser.zone) ? browser.zone : meridianZone(lon);
};
/** A place with its zone: its own when it names one this reader knows, else the one given to it here. */
export const withZone = (place: Omit<Fater3DPlace, 'zone'> & { zone?: string }, browser: { zone: string; offset: number }): Fater3DPlace =>
  ({ ...place, zone: place.zone !== undefined && validZone(place.zone) ? place.zone : zoneFor(place.lon, browser) });

const FORMATS = new Map<string, Intl.DateTimeFormat>();
// What a zone's own clock shows at an instant, read as if it were UTC: the difference is the zone's offset.
const wallClock = (zone: string, at: number) => {
  const format = FORMATS.get(zone) ?? new Intl.DateTimeFormat('en-US', { timeZone: zone, hourCycle: 'h23', year: 'numeric', month: 'numeric', day: 'numeric', hour: 'numeric', minute: 'numeric' });
  FORMATS.set(zone, format);
  const part = Object.fromEntries(format.formatToParts(at).map((p) => [p.type, Number(p.value)]));
  return Date.UTC(part.year!, part.month! - 1, part.day, part.hour, part.minute);
};
const OFFSETS = new Map<string, number>();
/**
 * How far a zone's clock is ahead of UTC at noon of a date (YYYY-MM-DD), in minutes, summer time included: the same
 * for every reader of the name. Kept once worked out: the 3D space asks at every change of the store. A name this
 * reader does not know reads as UTC.
 */
export const zoneOffset = (zone: string, date: string) => {
  const key = `${zone} ${date}`;
  const known = OFFSETS.get(key);
  if (known !== undefined) return known;
  const [year, month, day] = dayOf(date);
  const noon = Date.UTC(year, month - 1, day, 12);
  let offset = 0;
  try {
    // Twice: the first is the zone's offset at noon UTC, which near the date line is another day's.
    for (let pass = 0; pass < 2; pass += 1) offset = (wallClock(zone, noon - offset * 60_000) - (noon - offset * 60_000)) / 60_000;
  } catch { offset = 0; }
  if (OFFSETS.size > 2000) OFFSETS.clear();
  OFFSETS.set(key, offset);
  return offset;
};
export const zoneLabel = (offset: number) => {
  const minutes = Math.abs(offset) % 60;
  return offset ? `UTC${offset > 0 ? '+' : '-'}${Math.floor(Math.abs(offset) / 60)}${minutes ? `:${String(minutes).padStart(2, '0')}` : ''}` : 'UTC';
};
/** The hour of the day an instant falls at on a clock `offset` minutes ahead of UTC. */
export const hourOn = (at: Date, offset: number) => (((utcMinutes(at) + offset) % 1440) + 1440) % 1440 / 60;

/** The instant of a date (YYYY-MM-DD) and an hour of the day on a clock `offset` minutes ahead of UTC. */
export const sunInstant = (date: string, hours: number, offset: number) => {
  const [year, month, day] = dayOf(date);
  return new Date(Date.UTC(year, month - 1, day, 0, Math.round(hours * 60) - offset));
};

/** The place the sun is computed over: the one the user moved it to, else the project's own, else DEFAULT_PLACE. */
export const placeOf = (state: Pick<Fater3DState, 'place' | 'movedPlace'>): Fater3DPlace => state.movedPlace ?? state.place ?? DEFAULT_PLACE;
